import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, MessageBar, MessageBarBody, Spinner, Text } from "@fluentui/react-components";
import { Play20Regular } from "@fluentui/react-icons";
import { recordingUrl, recordings } from "../spurt/meetings";
import type { RecordingRef } from "../spurt/meetings";
import { asHttp, errText } from "./meetingErrors";
import { fmtDay, fmtClockTime, fmtDuration } from "./meetingFormat";
import css from "./Meetings.module.css";

const STILL_PROCESSING = "This recording is still being processed. Try again in a few minutes.";

/** The recordings of one meeting. "Play" asks for a time-limited address and plays it in a dialog. */
const RecordingsSection: React.FC<{ meetingId: string }> = ({ meetingId }) => {
  const [list, setList] = useState<RecordingRef[] | null>(null);
  const [error, setError] = useState("");
  const [play, setPlay] = useState<{ id: string; url: string } | null>(null);
  const [busyId, setBusyId] = useState("");
  const [playError, setPlayError] = useState<{ id: string; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await recordings(meetingId));
      setError("");
    } catch (e) {
      setError(errText(e, "see recordings"));
    }
  }, [meetingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (rec: RecordingRef) => {
    setBusyId(rec.id);
    setPlayError(null);
    try {
      const r = await recordingUrl(rec.id, "video");
      setPlay({ id: rec.id, url: r.url });
    } catch (e) {
      const st = asHttp(e).status;
      setPlayError({ id: rec.id, text: st === 404 || st === 409 ? STILL_PROCESSING : errText(e, "play this recording") });
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className={css.section} aria-label="Recordings">
      <h3 className={css.h3}>Recordings</h3>
      {error && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {!list && !error && <Spinner size="tiny" label="Loading recordings…" />}
      {list && list.length === 0 && <Text className={css.muted}>This meeting has no recordings.</Text>}
      {list && list.length > 0 && (
        <ul className={css.plain}>
          {list.map((r) => (
            <li key={r.id} className={css.recording}>
              <div>
                <div>
                  {fmtDay(r.startedMs)}, {fmtClockTime(r.startedMs)}
                  {r.endedMs ? ` (${fmtDuration(r.endedMs - r.startedMs)})` : ""}
                </div>
                <Badge appearance="tint" color={r.status === "finished" ? "success" : r.status === "failed" ? "danger" : "warning"}>
                  {r.status === "finished" ? "Ready" : r.status === "failed" ? "Failed" : "Recording"}
                </Badge>
                {r.status === "failed" && r.error && <Text className={css.muted}> {r.error}</Text>}
                {playError?.id === r.id && (
                  <Text className={css.warnText} role="alert" block>
                    {playError.text}
                  </Text>
                )}
              </div>
              {r.status === "finished" && (
                <Button icon={busyId === r.id ? <Spinner size="tiny" /> : <Play20Regular />} disabled={busyId !== ""} onClick={() => void open(r)} aria-label={`Play recording from ${fmtDay(r.startedMs)}`}>
                  Play
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!play} onOpenChange={(_, d) => !d.open && setPlay(null)}>
        <DialogSurface className={css.videoSurface}>
          <DialogBody>
            <DialogTitle action={<Button appearance="subtle" onClick={() => setPlay(null)}>Close</Button>}>Recording</DialogTitle>
            <DialogContent>
              {play && <video className={css.video} src={play.url} controls autoPlay playsInline aria-label="Meeting recording" />}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
};

export default RecordingsSection;
